import { AppState } from 'src/appState/AppState';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import { EventsListState } from 'src/domains/sportsbook/state/eventsListState/EventsListState';
import { UnifiedSportEventsListState } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedSportEventsListState';
import { OverUnderMarketCandidate, selectBestOverUnderMarketCandidate } from './selectBestOverUnderMarket';
import { preferOpenEventMarkets } from './preferOpenMarkets';
import { matchesMarketFilterTemplate } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/matchesMarketFilterTemplate';

const isOverUnderTemplate = (templateId: string): boolean => {
    return templateId.includes('over-under');
};

const getSelectionDecimalPrice = (market: MarketModel, selectionTemplateId: 'O' | 'U'): number | null => {
    const selection = market.selections.find((item) => {
        return item.templateId === selectionTemplateId;
    });

    const decimalPrice = selection?.price?.d;

    if (decimalPrice === undefined || Number.isFinite(decimalPrice) === false || decimalPrice <= 0) {
        return null;
    }

    return decimalPrice;
};

const getBestOverUnderMarketShort = (
    appState: AppState,
    matchingMarkets: Array<EventMarketItemType>
): EventMarketItemType | 'loading' | null => {
    const candidates: Array<OverUnderMarketCandidate> = [];
    let hasLoadingMarket = false;

    for (let index = 0; index < matchingMarkets.length; index++) {
        const marketShort = matchingMarkets[index];
        if (marketShort === undefined) {
            continue;
        }
        const marketModel = appState.appSportsBookState.models.getMarket(marketShort.id);

        if (marketModel === null) {
            hasLoadingMarket = true;
            continue;
        }

        candidates.push({
            id: marketShort.id,
            overPrice: getSelectionDecimalPrice(marketModel, 'O'),
            underPrice: getSelectionDecimalPrice(marketModel, 'U'),
            websiteMain: marketModel.websiteMain,
            displayOrder: marketModel.displayOrder,
            index,
        });
    }

    const bestCandidate = selectBestOverUnderMarketCandidate(candidates);

    if (bestCandidate !== null) {
        return (
            matchingMarkets.find((item) => {
                return item.id === bestCandidate.id;
            }) ?? null
        );
    }

    if (hasLoadingMarket === true) {
        return 'loading';
    }

    return null;
};

export const getMarketForDisplay = (
    appState: AppState,
    event: EventModel,
    eventsListState: EventsListState
): MarketModel | 'loading' | null => {
    if (eventsListState.filter_marketTemplateId === null) {
        const marketNew = event.marketsWithWebsiteMain[0]?.id2.getModel() ?? null;

        return marketNew;
    }

    const simpleMarkets = event.eventMarkets;

    if (simpleMarkets === null) {
        return 'loading';
    }

    const marketNewShort = simpleMarkets.find(
        (item) =>
            item.templateId === eventsListState.filter_marketTemplateId &&
            item.keyLine === eventsListState.filter_marketLine
    );

    if (marketNewShort === undefined) {
        return null;
    }

    const marketNew = appState.appSportsBookState.models.getMarket(marketNewShort.id);

    if (marketNew === null) {
        return 'loading';
    }

    return marketNew;
};

export const getMarketForDisplayUnified = (
    appState: AppState,
    event: EventModel,
    eventsListState: UnifiedSportEventsListState
): MarketModel | 'loading' | null => {
    if (eventsListState.filter_marketTemplateId === null) {
        const marketNew = event.marketsWithWebsiteMain[0]?.id2.getModel() ?? null;

        return marketNew;
    }

    const simpleMarkets = event.eventMarkets;

    if (simpleMarkets === null) {
        return 'loading';
    }

    const matchingMarkets = simpleMarkets.filter((item) => {
        const filterTemplateId = eventsListState.filter_marketTemplateId;
        if (matchesMarketFilterTemplate(filterTemplateId, item.templateId) === false) {
            return false;
        }

        if (eventsListState.filter_marketLine === null) {
            return true;
        }

        return item.keyLine === eventsListState.filter_marketLine;
    });

    // Drop suspended/hidden lines first, then pick which line to show.
    const marketsForLinePick = preferOpenEventMarkets(matchingMarkets);

    let marketNewShort: EventMarketItemType | undefined | 'loading' | null =
        marketsForLinePick.length <= 1 ? marketsForLinePick[0] : marketsForLinePick[1];

    if (
        marketsForLinePick.length > 1 &&
        eventsListState.filter_marketLine === null &&
        isOverUnderTemplate(eventsListState.filter_marketTemplateId)
    ) {
        marketNewShort = getBestOverUnderMarketShort(appState, marketsForLinePick);
    }

    if (marketNewShort === 'loading') {
        return 'loading';
    }

    if (marketNewShort === undefined || marketNewShort === null) {
        return null;
    }

    const marketNew = appState.appSportsBookState.models.getMarket(marketNewShort.id);

    if (marketNew === null) {
        return 'loading';
    }

    return marketNew;
};
